import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {useMaterialReactTable, MaterialReactTable} from 'material-react-table';
import {Box, Typography, Button, IconButton, Tooltip, Link} from '@mui/material';
import {Link as RouterLink, useNavigate} from 'react-router-dom';
import apiService from '../../services/apiService';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmDialog from '../../components/ConfirmDialog';
import {ROLES} from '../../config/roles';
import Protected from '../../components/Protected';

const CurrenciesPage = () => {
    const navigate = useNavigate();
    const [currencies, setCurrencies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const fetchCurrencies = useCallback(() => {
        setLoading(true);
        apiService.get('/api/currencies')
            .then(data => setCurrencies(data))
            .catch(err => setError(err.message || 'Failed to fetch currencies'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchCurrencies();
    }, [fetchCurrencies]);

    const handleDeleteItem = async () => {
        try {
            await apiService.delete(`/api/currencies/${itemToDelete.id}`);
            fetchCurrencies();
        } catch (err) {
            setError('Failed to delete currency.');
        } finally {
            setConfirmOpen(false);
            setItemToDelete(null);
        }
    };

    const columns = useMemo(
        () => [
            {
                accessorKey: 'id',
                header: 'ID',
                size: 50,
                Cell: ({cell}) => (
                    <Link component={RouterLink} to={`/currencies/${cell.getValue()}`}>
                        {cell.getValue()}
                    </Link>
                ),
            },
            {
                accessorKey: 'iconUrl',
                header: 'Icon',
                size: 60,
                Cell: ({cell, row}) => (
                    <Box
                        component="img"
                        src={cell.getValue()}
                        alt={row.original.name}
                        sx={{width: 40, height: 40, borderRadius: '50%', objectFit: 'cover'}}
                    />
                ),
            },
            {
                accessorKey: 'identifier',
                header: 'Identifier',
                size: 80,
            },
            {
                accessorKey: 'name',
                header: 'Name',
                size: 120,
                Cell: ({cell, row}) => (
                    <Link component={RouterLink} to={`/currencies/${row.original.id}`}>
                        {cell.getValue()}
                    </Link>
                ),
            },
            {
                accessorKey: 'sortOrder',
                header: 'Sort Order',
                size: 80,
            },
        ],
        []
    );

    const table = useMaterialReactTable({
        columns,
        data: currencies,
        enablePagination: false,
        enableBottomToolbar: false,
        state: {isLoading: loading, showAlertBanner: !!error},
        muiToolbarAlertBannerProps: error ? {color: 'error', children: `Error: ${error}`} : undefined,
        initialState: {
            showGlobalFilter: true,
            sorting: [{id: 'sortOrder', desc: false}],
        },
        renderTopToolbarCustomActions: () => (
            <Protected component={CurrenciesPage} action="create">
                <Button variant="contained" startIcon={<AddIcon/>} onClick={() => navigate('/currencies/new')}>
                    Create Currency
                </Button>
            </Protected>
        ),
        enableRowActions: true,
        displayColumnDefOptions: {'mrt-row-actions': {header: 'Actions', size: 100}},
        positionActionsColumn: 'last',
        renderRowActions: ({row}) => (
            <Box sx={{display: 'flex', gap: '0.5rem'}}>
                <Tooltip title="Edit">
                    <IconButton component={RouterLink} to={`/currencies/${row.original.id}`}>
                        <EditIcon/>
                    </IconButton>
                </Tooltip>
                <Protected component={CurrenciesPage} action="delete">
                    <Tooltip title="Delete">
                        <IconButton color="error" onClick={() => {
                            setItemToDelete(row.original);
                            setConfirmOpen(true);
                        }}>
                            <DeleteIcon/>
                        </IconButton>
                    </Tooltip>
                </Protected>
            </Box>
        ),
    });

    return (
        <Box sx={{p: 2}}>
            <Typography variant="h4" gutterBottom>Currencies</Typography>
            <MaterialReactTable table={table}/>
            <Protected component={CurrenciesPage} action="delete">
                <ConfirmDialog
                    open={confirmOpen}
                    onClose={() => setConfirmOpen(false)}
                    onConfirm={handleDeleteItem}
                    title={`Delete Currency: ${itemToDelete?.name}?`}
                    message="Are you sure you want to delete this currency? This action cannot be undone."
                />
            </Protected>
        </Box>
    );
};

CurrenciesPage.abilities = {
    read: [ROLES.VIEWERS, ROLES.SUPPORT, ROLES.ADMINS],
    create: [ROLES.ADMINS],
    delete: [ROLES.ADMINS],
};

export default CurrenciesPage;
